import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styles from './DatasetStatsAdmin.module.css';
import {
    downloadDatasetStatsCsvAdmin,
    fetchDatasetStatsAdmin,
} from '../../../api/datasetStatsAdmin';
import {ADMIN_SESSION_EXPIRED_EVENT, isSessionExpiredError} from '../../../utils/authErrors';

const TIME_SPANS = [
    {value: '1w', label: '1W'},
    {value: '1m', label: '1M'},
    {value: '1y', label: '1Y'},
    {value: 'all', label: 'All'},
    {value: 'custom', label: 'Custom'},
];

const DEFAULT_ROBOT_OPTIONS = [
    {value: 'all', label: 'All robots'},
];

const DEFAULT_OPERATOR_OPTIONS = [
    {value: '', label: 'All operators'},
];

const EMPTY_STATS = {
    robot_types: DEFAULT_ROBOT_OPTIONS,
    operator_ids: DEFAULT_OPERATOR_OPTIONS,
    tasks: [],
    summary: {
        episode_count: 0,
        with_duration_count: 0,
        failure_rate: 0,
        pass_fail_count: 0,
        mean_duration_s: 0,
        total_duration_s: 0,
    },
    histogram: [],
    histogram_x_labels: ['0', '0', '0'],
    review_status: [],
    failure_comments: [],
};

function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function formatInt(value) {
    return Math.round(toNumber(value)).toLocaleString();
}

export function formatHours(value) {
    return `${toNumber(value).toFixed(1)}h`;
}

export function formatRatioPercent(value) {
    return `${(toNumber(value) * 100).toFixed(1)}%`;
}

export function formatPercentage(value) {
    return `${toNumber(value).toFixed(1)}%`;
}

function formatSeconds(value) {
    const seconds = toNumber(value);
    return `${seconds.toFixed(1)}s`;
}

export function formatDuration(value) {
    const seconds = toNumber(value);
    if (seconds >= 3600) {
        return `${(seconds / 3600).toFixed(1)}h`;
    }
    if (seconds >= 60) {
        return `${(seconds / 60).toFixed(1)}m`;
    }
    return formatSeconds(seconds);
}

function percentOf(count, total) {
    const denominator = toNumber(total);
    if (denominator <= 0) return 0;
    return (toNumber(count) / denominator) * 100;
}

function sanitizeFailureLabel(value) {
    return String(value || '')
        .split(/,?\s*schema\s+schema\b/i)[0]
        .trim();
}

function normalizeTask(row) {
    if (typeof row === 'string') return {name: row, count: 0, total_hours: 0};
    return {
        name: String(row?.name ?? row?.task ?? row?.task_name ?? row?.scenario ?? 'Untitled task'),
        count: toNumber(row?.count ?? row?.episode_count ?? row?.episodes ?? 0),
        total_hours: toNumber(row?.total_hours ?? row?.total_duration_hours ?? 0),
    };
}

function normalizeRobotOption(row) {
    if (typeof row === 'string') {
        const value = row.toLowerCase().replace(/\s+/g, '_');
        return {value, label: row};
    }
    return {
        value: String(row?.value ?? row?.robot_type ?? row?.id ?? '').trim() || 'all',
        label: String(row?.label ?? row?.name ?? row?.robot_type ?? 'All robots'),
    };
}

function normalizeOperatorOption(row) {
    if (typeof row === 'string' || typeof row === 'number') {
        const value = String(row).trim();
        return {value, label: value};
    }
    const value = String(row?.value ?? row?.operator_id ?? row?.user_id ?? row?.id ?? '').trim();
    const email = String(row?.email ?? row?.operator_email ?? '').trim();
    return {
        value,
        label: String(row?.label ?? (value && email ? `${value} · ${email}` : value || 'All operators')),
    };
}

function normalizeRows(rows, total) {
    return (Array.isArray(rows) ? rows : [])
        .map((row) => {
            const count = toNumber(row?.count ?? row?.episode_count ?? 0);
            return {
                label: sanitizeFailureLabel(row?.label ?? row?.status ?? row?.review_status ?? row?.failure_label ?? ''),
                count,
                // Displayed counts and their denominator are the source of truth. This
                // avoids stale or differently-scaled percentages from an API response.
                percentage: percentOf(count, total),
            };
        })
        .filter((row) => row.label);
}

export function normalizeDatasetStats(raw) {
    if (!raw || typeof raw !== 'object') return EMPTY_STATS;

    const tasks = Array.isArray(raw.tasks) ? raw.tasks.map(normalizeTask) : EMPTY_STATS.tasks;
    const robotTypes = Array.isArray(raw.robot_types)
        ? raw.robot_types.map(normalizeRobotOption)
        : Array.isArray(raw.robotTypes)
            ? raw.robotTypes.map(normalizeRobotOption)
            : EMPTY_STATS.robot_types;
    const hasAllRobots = robotTypes.some((option) => option.value === 'all');
    const operatorIds = Array.isArray(raw.operator_ids)
        ? raw.operator_ids.map(normalizeOperatorOption)
        : Array.isArray(raw.operatorIds)
            ? raw.operatorIds.map(normalizeOperatorOption)
            : EMPTY_STATS.operator_ids;
    const hasAllOperators = operatorIds.some((option) => option.value === '');
    const summary = raw.summary || raw.stats || {};
    const episodeCount = toNumber(summary.episode_count ?? summary.episodes ?? raw.episode_count ?? 0);
    const declaredPassFailCount = toNumber(summary.pass_fail_count ?? summary.reviewed_count ?? episodeCount);
    const histogramRows = Array.isArray(raw.histogram)
        ? raw.histogram.map((row, index) => ({
            bin_start_s: row?.bin_start_s ?? row?.min_s ?? row?.start ?? index,
            bin_end_s: row?.bin_end_s ?? row?.max_s ?? row?.end ?? index + 1,
            count: toNumber(row?.count ?? row?.episode_count ?? 0),
            label: row?.label,
        }))
        : EMPTY_STATS.histogram;
    const histogramTotal = histogramRows.reduce((total, row) => total + row.count, 0);
    const rawReviewRows = raw.review_status ?? raw.reviewStatus;
    const reviewTotal = Array.isArray(rawReviewRows)
        ? rawReviewRows.reduce((total, row) => total + toNumber(row?.count ?? row?.episode_count ?? 0), 0)
        : 0;
    const passFailCount = reviewTotal || declaredPassFailCount;
    const reviewRows = normalizeRows(rawReviewRows, passFailCount);
    const failureReviewRow = reviewRows.find((row) => row.label.toUpperCase() === 'FAIL');
    const failureRate = reviewTotal > 0
        ? failureReviewRow?.count / reviewTotal || 0
        : toNumber(summary.failure_rate ?? summary.fail_rate ?? 0);
    const rawFailureRows = raw.failure_comments ?? raw.failureComments ?? raw.failure_labels;
    const failureTotal = Array.isArray(rawFailureRows)
        ? rawFailureRows.reduce((total, row) => total + toNumber(row?.count ?? row?.episode_count ?? 0), 0)
        : 0;
    const failureRows = normalizeRows(rawFailureRows, failureTotal);

    return {
        robot_types: hasAllRobots ? robotTypes : [DEFAULT_ROBOT_OPTIONS[0], ...robotTypes],
        operator_ids: hasAllOperators ? operatorIds : [DEFAULT_OPERATOR_OPTIONS[0], ...operatorIds],
        tasks,
        summary: {
            episode_count: episodeCount,
            with_duration_count: histogramRows.length
                ? histogramTotal
                : toNumber(summary.with_duration_count ?? summary.duration_count ?? summary.count_with_duration ?? episodeCount),
            failure_rate: failureRate,
            pass_fail_count: passFailCount,
            mean_duration_s: toNumber(summary.mean_duration_s ?? summary.mean_duration ?? summary.avg_duration_s ?? 0),
            total_duration_s: toNumber(summary.total_duration_s ?? summary.total_duration ?? 0),
        },
        histogram: histogramRows,
        histogram_x_labels: raw.histogram_x_labels || raw.histogramXLabels || null,
        review_status: reviewRows.length ? reviewRows : EMPTY_STATS.review_status,
        failure_comments: failureRows.length ? failureRows : EMPTY_STATS.failure_comments,
    };
}

function downloadBlob(blob, fileName) {
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
}

function resolveFileName(response, fallback) {
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match?.[1] || fallback;
}

function buildCsvFileName(query) {
    const safeTask = String(query.task || 'all-tasks').replace(/[^a-z0-9_-]+/gi, '_');
    return `${safeTask}_${query.timeSpan || 'all'}_${new Date().toISOString().slice(0, 10)}.csv`;
}

const DatasetStatsAdmin = ({adminToken: adminTokenProp = ''}) => {
    const adminToken = adminTokenProp || localStorage.getItem('prismax_admin_token') || '';
    const [robotType, setRobotType] = useState('all');
    const [operatorId, setOperatorId] = useState('');
    const [timeSpan, setTimeSpan] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [hideTask12, setHideTask12] = useState(true);
    const [activeTask, setActiveTask] = useState('All tasks');
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    const queryState = useMemo(() => ({
        task: activeTask,
        robotType,
        operatorId: operatorId.trim(),
        timeSpan,
        dateFrom,
        dateTo,
        hideTask12,
    }), [activeTask, dateFrom, dateTo, hideTask12, operatorId, robotType, timeSpan]);

    const loadStats = useCallback(async (signal) => {
        if (!adminToken) {
            setStats(null);
            setIsLoading(false);
            setStatusMessage('');
            return;
        }
        setIsLoading(true);
        setStatusMessage('');
        try {
            const payload = await fetchDatasetStatsAdmin({
                token: adminToken,
                query: queryState,
                signal,
            });
            const normalized = normalizeDatasetStats(payload);
            setStats(normalized);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            if (isSessionExpiredError(error)) {
                window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT));
                return;
            }
            console.warn('Dataset stats API unavailable:', error);
            setStats(null);
            setStatusMessage('');
        } finally {
            setIsLoading(false);
        }
    }, [adminToken, queryState]);

    useEffect(() => {
        const controller = new AbortController();
        void loadStats(controller.signal);
        return () => controller.abort();
    }, [loadStats]);

    const hasLoadedStats = Boolean(stats);
    const tasks = stats?.tasks?.length ? stats.tasks : [];
    const robotOptions = stats?.robot_types?.length ? stats.robot_types : DEFAULT_ROBOT_OPTIONS;
    const operatorOptions = stats?.operator_ids?.length ? stats.operator_ids : DEFAULT_OPERATOR_OPTIONS;
    const summary = stats?.summary || {};
    const histogram = stats?.histogram || [];
    const maxHistogramCount = Math.max(1, ...histogram.map((row) => toNumber(row.count)));
    const yAxisValues = [
        maxHistogramCount,
        Math.round(maxHistogramCount * 0.75),
        Math.round(maxHistogramCount * 0.5),
        Math.round(maxHistogramCount * 0.25),
        0,
    ];
    const histogramXLabels = stats?.histogram_x_labels || [
        histogram[0]?.bin_start_s ?? 0,
        histogram[Math.floor(histogram.length / 2)]?.bin_start_s ?? '',
        histogram[histogram.length - 1]?.bin_end_s ?? '',
    ];
    const reviewRows = stats?.review_status || [];
    const failureRows = stats?.failure_comments || [];
    const maxReviewCount = Math.max(1, ...reviewRows.map((row) => toNumber(row.count)));

    const handleDownloadCsv = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        setStatusMessage('');
        const fallbackName = buildCsvFileName(queryState);
        try {
            if (!adminToken) {
                throw new Error('Dataset stats export API unavailable.');
            }
            const response = await downloadDatasetStatsCsvAdmin({
                token: adminToken,
                query: queryState,
            });
            const blob = await response.blob();
            downloadBlob(blob, resolveFileName(response, fallbackName));
        } catch (error) {
            if (isSessionExpiredError(error)) {
                window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT));
                return;
            }
            setStatusMessage('Failed to download dataset stats CSV.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className={styles.datasetStats}>
            <aside className={styles.sidebar}>
                <div className={styles.filters}>
                    <div className={styles.filterGroup}>
                        <div className={styles.filterLabel}>Robot type</div>
                        <div className={styles.selectWrap}>
                            <select
                                className={styles.filterSelect}
                                value={robotType}
                                onChange={(event) => setRobotType(event.target.value)}
                            >
                                {robotOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <span className={styles.selectChevron}>
                                <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden="true">
                                    <path d="M1 1l3.5 3L8 1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    <div className={styles.filterGroup}>
                        <div className={styles.filterLabel}>Operator ID</div>
                        <div className={styles.selectWrap}>
                            <select
                                className={styles.filterSelect}
                                value={operatorId}
                                onChange={(event) => setOperatorId(event.target.value)}
                            >
                                {operatorOptions.map((option) => (
                                    <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <span className={styles.selectChevron}>
                                <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden="true">
                                    <path d="M1 1l3.5 3L8 1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    <div className={styles.filterGroup}>
                        <div className={styles.filterLabel}>Time span</div>
                        <div className={styles.chipRow}>
                            {TIME_SPANS.map((span) => (
                                <button
                                    key={span.value}
                                    type="button"
                                    className={`${styles.chip} ${timeSpan === span.value ? styles.chipActive : ''}`}
                                    onClick={() => setTimeSpan(span.value)}
                                >
                                    {span.label}
                                </button>
                            ))}
                        </div>
                        <div className={`${styles.dateRange} ${timeSpan === 'custom' ? styles.dateRangeVisible : ''}`}>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={dateFrom}
                                onChange={(event) => setDateFrom(event.target.value)}
                                aria-label="From"
                            />
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={dateTo}
                                onChange={(event) => setDateTo(event.target.value)}
                                aria-label="To"
                            />
                        </div>
                    </div>

                    <label className={styles.checkboxRow}>
                        <input
                            type="checkbox"
                            checked={hideTask12}
                            onChange={(event) => {
                                setHideTask12(event.target.checked);
                                setActiveTask('All tasks');
                            }}
                        />
                        <span>Hide test data (task_id 12)</span>
                    </label>
                </div>

                <div className={styles.sidebarTitle}>Tasks</div>
                <div>
                    {!hasLoadedStats ? (
                        <div className={styles.loadingBlock}>Loading dataset stats...</div>
                    ) : tasks.map((task) => (
                        <button
                            key={task.name}
                            type="button"
                            className={`${styles.taskItem} ${activeTask === task.name ? styles.taskItemActive : ''}`}
                            onClick={() => setActiveTask(task.name)}
                        >
                            <span className={styles.taskName}>{task.name}</span>
                            <span className={styles.taskCount}>
                                {formatInt(task.count)} ({formatHours(task.total_hours)})
                            </span>
                        </button>
                    ))}
                </div>
            </aside>

            <main className={styles.content}>
                <div className={styles.contentHeadRow}>
                    <h1 className={styles.contentHead}>{activeTask}</h1>
                    <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={handleDownloadCsv}
                        disabled={isDownloading || !hasLoadedStats}
                    >
                        <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M7 1.5v7M4 6l3 3 3-3M2 11.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {isDownloading ? 'Downloading...' : 'Download CSV'}
                    </button>
                </div>

                {(isLoading || statusMessage) && (
                    <div className={`${styles.statusLine} ${statusMessage ? styles.statusError : ''}`}>
                        {isLoading ? 'Loading dataset stats...' : statusMessage}
                    </div>
                )}

                {!hasLoadedStats ? (
                    <div className={styles.contentLoading}>Loading dataset stats...</div>
                ) : (
                    <>

                <div className={styles.statRow}>
                    <div className={styles.statBox}>
                        <div className={styles.statNum}>{formatInt(summary.episode_count)}</div>
                        <div className={styles.statLabel}>Episodes</div>
                    </div>
                    <div className={styles.statBox}>
                        <div className={styles.statNum}>{formatRatioPercent(summary.failure_rate)}</div>
                        <div className={styles.statLabel}>Failure rate</div>
                        <div className={styles.statSub}>of {formatInt(summary.pass_fail_count)} pass/fail</div>
                    </div>
                    <div className={styles.statBox}>
                        <div className={styles.statNum}>{formatSeconds(summary.mean_duration_s)}</div>
                        <div className={styles.statLabel}>Mean duration</div>
                    </div>
                    <div className={styles.statBox}>
                        <div className={styles.statNum}>{formatDuration(summary.total_duration_s)}</div>
                        <div className={styles.statLabel}>Total duration</div>
                    </div>
                </div>

                <section className={styles.section}>
                    <div className={styles.sectionTitle}>Duration distribution</div>
                    <div className={styles.card}>
                        <div className={styles.hist}>
                            <div className={styles.histYaxis}>
                                {yAxisValues.map((value, index) => (
                                    <span key={`${value}-${index}`}>{formatInt(value)}</span>
                                ))}
                            </div>
                            {histogram.map((row, index) => (
                                <div
                                    key={`${row.bin_start_s}-${row.bin_end_s}-${index}`}
                                    className={styles.bar}
                                    style={{height: `${Math.max(1, (toNumber(row.count) / maxHistogramCount) * 100)}%`}}
                                    title={`${formatInt(row.count)} episodes`}
                                />
                            ))}
                        </div>
                        <div className={styles.histXaxis}>
                            {histogramXLabels.map((label, index) => (
                                <span key={`${label}-${index}`}>{label}</span>
                            ))}
                        </div>
                        <div className={styles.histXlabel}>duration (s)</div>
                    </div>
                </section>

                <div className={styles.lowerGrid}>
                    <section className={`${styles.section} ${styles.sectionTight}`}>
                        <div className={styles.sectionTitle}>Review status</div>
                        <div className={styles.card}>
                            <div className={styles.rsBars}>
                                {reviewRows.map((row) => {
                                    const label = String(row.label || '').toUpperCase();
                                    const height = Math.max(1, (toNumber(row.count) / maxReviewCount) * 100);
                                    return (
                                        <div className={styles.rsCol} key={label}>
                                            <span className={styles.rsCount}>{formatInt(row.count)}</span>
                                            <div
                                                className={`${styles.rsBar} ${label === 'FAIL' ? styles.rsFail : styles.rsPass}`}
                                                style={{height: `${height}%`}}
                                            />
                                            <span className={styles.rsLabel}>{label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    <th className={styles.num}>Count</th>
                                    <th className={styles.num}>%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewRows.map((row) => (
                                    <tr key={row.label}>
                                        <td>{String(row.label).toUpperCase()}</td>
                                        <td className={styles.num}>{formatInt(row.count)}</td>
                                        <td className={`${styles.num} ${styles.pct}`}>{formatPercentage(row.percentage)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section className={`${styles.section} ${styles.sectionTight}`}>
                        <div className={styles.sectionTitle}>Failure comments</div>
                        <div className={`${styles.card} ${styles.tableCard}`}>
                            <table className={styles.dataTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.failureLabelHead}>Label</th>
                                        <th className={styles.num}>Count</th>
                                        <th className={`${styles.num} ${styles.failurePctHead}`}>% of labels</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failureRows.length ? failureRows.map((row) => (
                                        <tr key={row.label}>
                                            <td className={styles.failureLabelCell}>{row.label}</td>
                                            <td className={styles.num}>{formatInt(row.count)}</td>
                                            <td className={`${styles.num} ${styles.pct} ${styles.failurePctCell}`}>{formatPercentage(row.percentage)}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td className={styles.emptyState} colSpan="3">No failure comments.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default DatasetStatsAdmin;
